import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Leaf, LogOut, Mail, Package, Pencil, Plus, Settings as SettingsIcon, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fetchSettings, formatApiErrorDetail } from "../lib/api";
import { LOGO_URL } from "../lib/site";

const CATEGORIES = ["spices", "fruits", "nursery", "vegetables"];
const emptyForm = {
  name: "",
  category: "spices",
  tag: "",
  group: "",
  description: "",
  story: "",
  rehydration: "",
  availability: "available",
  display_order: 0,
  images: [{ url: "", alt: "" }],
};

function ProductFormModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    initial
      ? {
          name: initial.name,
          category: initial.category,
          tag: initial.tag || "",
          group: initial.group || "",
          description: initial.description || "",
          story: initial.story || "",
          rehydration: initial.rehydration || "",
          availability: initial.availability,
          display_order: initial.display_order || 0,
          images: initial.images?.length ? initial.images.map((i) => ({ url: i.url, alt: i.alt || "" })) : [{ url: "", alt: "" }],
        }
      : emptyForm
  );
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const setImage = (i, key, value) => {
    const images = form.images.map((img, j) => (j === i ? { ...img, [key]: value } : img));
    setForm({ ...form, images });
  };

  const generateCopy = async () => {
    if (!form.name.trim()) return toast.error("Enter the product name first");
    setAiBusy(true);
    try {
      const r = await api.post("/ai/product-copy", {
        name: form.name.trim(),
        category: form.category,
        tag: form.tag,
        notes: form.description,
      });
      setForm({
        ...form,
        description: r.data.description || form.description,
        story: r.data.story || form.story,
        images: form.images.map((im) => (im.alt ? im : { ...im, alt: r.data.alt || im.alt })),
      });
      toast.success("Claude wrote the copy — review and tweak as you like");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const images = form.images.filter((i) => i.url.trim());
    if (!form.name.trim()) return toast.error("Product name is required");
    if (!images.length) return toast.error("Add at least one image URL");
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim(), images, display_order: Number(form.display_order) || 0 };
      if (initial) {
        await api.put(`/products/${initial.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6">
      <div className="absolute inset-0 bg-nightgreen/70 backdrop-blur-sm" onClick={onClose} />
      <div
        data-testid="product-form-modal"
        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-cream rounded-t-3xl sm:rounded-3xl shadow-2xl p-7"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl font-semibold text-forest">{initial ? "Edit Product" : "Add Product"}</h3>
          <button data-testid="product-form-close" onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full bg-beige flex items-center justify-center hover:bg-sand">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Name</label>
              <input
                data-testid="product-form-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Category</label>
              <select
                data-testid="product-form-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c[0].toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Label / Tag</label>
              <input
                data-testid="product-form-tag"
                value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })}
                placeholder="e.g. Daily Essential"
                className="w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Sub-section</label>
              <select
                data-testid="product-form-group"
                value={form.group}
                onChange={(e) => setForm({ ...form, group: e.target.value })}
                className="w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              >
                <option value="">None</option>
                <option value="a">A — Everyday Essentials</option>
                <option value="b">B — Premium & Superfood</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Rehydration</label>
              <input
                data-testid="product-form-rehydration"
                value={form.rehydration}
                onChange={(e) => setForm({ ...form, rehydration: e.target.value })}
                placeholder="e.g. 5 mins in warm water"
                className="w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              />
            </div>
          </div>

          <button
            type="button"
            data-testid="ai-generate-btn"
            onClick={generateCopy}
            disabled={aiBusy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-gold/50 bg-gradient-to-r from-[#FBF3E4] to-[#F3E7CE] px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-soil transition-all hover:shadow-md hover:border-gold disabled:opacity-60"
          >
            <Sparkles className={`w-4 h-4 text-gold ${aiBusy ? "animate-pulse" : ""}`} />
            {aiBusy ? "Claude is writing…" : "Write copy with Claude AI"}
          </button>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Description</label>
            <textarea
              data-testid="product-form-description"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60 resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Farm Story</label>
            <textarea
              data-testid="product-form-story"
              rows={2}
              value={form.story}
              onChange={(e) => setForm({ ...form, story: e.target.value })}
              className="w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60 resize-none"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Display Order</label>
              <input
                data-testid="product-form-order"
                type="number"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                className="w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Availability</label>
              <select
                data-testid="product-form-availability"
                value={form.availability}
                onChange={(e) => setForm({ ...form, availability: e.target.value })}
                className="w-full rounded-xl border border-sand bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
              >
                <option value="available">Available</option>
                <option value="coming_soon">Coming Soon</option>
                <option value="to_confirm">Availability to be confirmed</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">Images</label>
            <div className="space-y-2.5">
              {form.images.map((img, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    data-testid={`product-form-image-url-${i}`}
                    value={img.url}
                    onChange={(e) => setImage(i, "url", e.target.value)}
                    placeholder="Image URL"
                    className="flex-1 min-w-0 rounded-xl border border-sand bg-white px-3.5 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-gold/60"
                  />
                  <input
                    data-testid={`product-form-image-alt-${i}`}
                    value={img.alt}
                    onChange={(e) => setImage(i, "alt", e.target.value)}
                    placeholder="Alt text"
                    className="w-32 rounded-xl border border-sand bg-white px-3.5 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-gold/60"
                  />
                  <button
                    type="button"
                    data-testid={`product-form-image-remove-${i}`}
                    onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })}
                    aria-label="Remove image"
                    className="w-9 h-9 rounded-lg bg-beige flex items-center justify-center text-forest hover:bg-red-50 hover:text-red-700 shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              data-testid="product-form-add-image"
              onClick={() => setForm({ ...form, images: [...form.images, { url: "", alt: "" }] })}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gold hover:text-forest"
            >
              <Plus className="w-4 h-4" />
              Add image
            </button>
          </div>

          <button
            data-testid="save-product-btn"
            type="submit"
            disabled={saving}
            className="w-full rounded-full bg-forest px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-cream transition-all hover:bg-nightgreen disabled:opacity-60"
          >
            {saving ? "Saving…" : initial ? "Save Changes" : "Add Product"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Could not load products"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const AVAIL_CYCLE = { available: "coming_soon", coming_soon: "to_confirm", to_confirm: "available" };
  const AVAIL_STYLE = {
    available: { label: "Available", cls: "bg-[#EBF2EC] text-forest border-[#B8D4BD] hover:bg-[#D8E8DC]" },
    coming_soon: { label: "Coming Soon", cls: "bg-[#F3ECE0] text-[#A87B32] border-[#DECDB3] hover:bg-[#EBDCC2]" },
    to_confirm: { label: "To Confirm", cls: "bg-[#F3ECE0] text-soil border-[#DECDB3] hover:bg-sand" },
  };

  const toggleAvailability = async (p) => {
    const nextAvail = AVAIL_CYCLE[p.availability] || "available";
    try {
      await api.put(`/products/${p.id}`, {
        name: p.name,
        category: p.category,
        tag: p.tag || "",
        group: p.group || "",
        description: p.description,
        story: p.story,
        rehydration: p.rehydration || "",
        images: p.images,
        display_order: p.display_order,
        availability: nextAvail,
      });
      toast.success(`${p.name} → ${(AVAIL_STYLE[nextAvail] || AVAIL_STYLE.available).label}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" permanently?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl font-semibold text-forest">Products ({products.length})</h2>
        <button
          data-testid="add-product-btn"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-nightgreen hover:bg-forest hover:text-cream transition-all"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-muted py-10 text-center">Loading products…</p>
      ) : (
        <div className="space-y-3">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`product-row-${p.slug}`}
              className="flex flex-wrap items-center gap-4 rounded-2xl bg-white border border-sand p-4"
            >
              {p.images?.[0] && (
                <img src={p.images[0].url} alt={p.images[0].alt || p.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg font-semibold text-forest truncate">{p.name}</p>
                <p className="text-xs text-muted capitalize">
                  {p.category}
                  {p.tag ? ` · ${p.tag}` : ""} · order {p.display_order} · {p.images?.length || 0} images
                </p>
              </div>
              <button
                data-testid={`toggle-availability-${p.slug}`}
                onClick={() => toggleAvailability(p)}
                className={`rounded-full px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] border transition-all ${
                  (AVAIL_STYLE[p.availability] || AVAIL_STYLE.available).cls
                }`}
              >
                {(AVAIL_STYLE[p.availability] || AVAIL_STYLE.available).label}
              </button>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`edit-product-${p.slug}`}
                  onClick={() => {
                    setEditing(p);
                    setFormOpen(true);
                  }}
                  aria-label={`Edit ${p.name}`}
                  className="w-9 h-9 rounded-lg bg-beige flex items-center justify-center text-forest hover:bg-gold hover:text-nightgreen transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`delete-product-${p.slug}`}
                  onClick={() => remove(p)}
                  aria-label={`Delete ${p.name}`}
                  className="w-9 h-9 rounded-lg bg-beige flex items-center justify-center text-forest hover:bg-red-50 hover:text-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {products.length === 0 && <p className="text-sm text-muted py-10 text-center">No products yet. Add your first one.</p>}
        </div>
      )}
      {formOpen && <ProductFormModal initial={editing} onClose={() => setFormOpen(false)} onSaved={load} />}
    </div>
  );
}

function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/enquiries")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load enquiries"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const remove = async (id) => {
    await api.delete(`/enquiries/${id}`);
    setItems(items.filter((i) => i.id !== id));
    toast.success("Enquiry removed");
  };

  return (
    <div data-testid="admin-enquiries-tab">
      <h2 className="font-serif text-2xl font-semibold text-forest mb-5">Enquiries ({items.length})</h2>
      {loading ? (
        <p className="text-sm text-muted py-10 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted py-10 text-center">No enquiries yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((e) => (
            <div key={e.id} data-testid={`enquiry-row-${e.id}`} className="rounded-2xl bg-white border border-sand p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-forest text-sm">
                    {e.name} {e.phone && <span className="text-muted font-normal">· {e.phone}</span>}
                  </p>
                  {e.product_name && <p className="text-xs text-gold mt-0.5">Product: {e.product_name}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                  <button
                    data-testid={`delete-enquiry-${e.id}`}
                    onClick={() => remove(e.id)}
                    aria-label="Delete enquiry"
                    className="w-8 h-8 rounded-lg bg-beige flex items-center justify-center text-forest hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <p className="mt-3 text-sm font-light text-ink/80 leading-relaxed">{e.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotifyTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/notify")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load notify list"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const remove = async (id) => {
    await api.delete(`/notify/${id}`);
    setItems(items.filter((i) => i.id !== id));
    toast.success("Removed from notify list");
  };

  return (
    <div data-testid="admin-notify-tab">
      <h2 className="font-serif text-2xl font-semibold text-forest mb-5">Notify-Me List ({items.length})</h2>
      {loading ? (
        <p className="text-sm text-muted py-10 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted py-10 text-center">No subscribers yet.</p>
      ) : (
        <div className="rounded-2xl bg-white border border-sand overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-beige text-left text-xs uppercase tracking-wide text-forest/70">
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Product</th>
                <th className="px-5 py-3 hidden sm:table-cell">Date</th>
                <th className="px-5 py-3 w-14"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} data-testid={`notify-row-${n.id}`} className="border-t border-sand/60">
                  <td className="px-5 py-3.5 text-forest">{n.email}</td>
                  <td className="px-5 py-3.5">{n.product_name || "—"}</td>
                  <td className="px-5 py-3.5 text-muted hidden sm:table-cell">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ""}</td>
                  <td className="px-5 py-3.5">
                    <button
                      data-testid={`delete-notify-${n.id}`}
                      onClick={() => remove(n.id)}
                      aria-label="Remove subscriber"
                      className="w-8 h-8 rounded-lg bg-beige flex items-center justify-center text-forest hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const SETTINGS_FIELDS = [
  { key: "phone", label: "Phone (display)" },
  { key: "whatsapp", label: "WhatsApp number (country code, digits only)" },
  { key: "email", label: "Email" },
  { key: "address", label: "Farm address" },
  { key: "maps_url", label: "Google Maps link (Get Directions)" },
  { key: "maps_embed", label: "Google Maps embed URL" },
  { key: "instagram", label: "Instagram URL" },
  { key: "facebook", label: "Facebook URL" },
  { key: "youtube", label: "YouTube URL" },
];

function SettingsTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings().then(setForm).catch(() => toast.error("Could not load settings"));
  }, []);

  if (!form) return <p className="text-sm text-muted py-10 text-center">Loading…</p>;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/settings", form);
      setForm(r.data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-tab">
      <h2 className="font-serif text-2xl font-semibold text-forest mb-5">Contact & Farm Details</h2>
      <form onSubmit={submit} className="rounded-2xl bg-white border border-sand p-6 space-y-4 max-w-2xl">
        {SETTINGS_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-xs font-semibold uppercase tracking-wide text-forest/70 mb-1.5">{f.label}</label>
            <input
              data-testid={`settings-input-${f.key}`}
              value={form[f.key] || ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="w-full rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
            />
          </div>
        ))}
        <button
          data-testid="save-settings-btn"
          type="submit"
          disabled={saving}
          className="rounded-full bg-forest px-8 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-cream hover:bg-nightgreen transition-all disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </form>
    </div>
  );
}

const TABS = [
  { key: "products", label: "Products", icon: Package },
  { key: "enquiries", label: "Enquiries", icon: Mail },
  { key: "notify", label: "Notify List", icon: Bell },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("products");

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setAdmin(r.data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    navigate("/admin/login");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center">
        <Leaf className="w-8 h-8 text-gold animate-pulse" />
      </div>
    );
  }
  if (!admin) return null;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-beige">
      <header className="sticky top-0 z-40 bg-forest text-cream shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_URL} alt="Unique Organic Farm logo" className="w-9 h-9 rounded-full ring-1 ring-gold/40 object-cover" />
            <div className="min-w-0">
              <p className="font-serif font-semibold leading-tight truncate">Farm Admin</p>
              <p className="text-[10px] text-cream/60 truncate">{admin.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="hidden sm:block text-xs text-cream/70 hover:text-goldlight transition-colors mr-2">
              View site
            </a>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-full border border-cream/30 px-4 py-2 text-xs font-semibold uppercase tracking-wide hover:bg-cream hover:text-forest transition-all"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] whitespace-nowrap transition-all ${
                tab === t.key ? "bg-gold text-nightgreen" : "bg-cream/10 text-cream/80 hover:bg-cream/20"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {tab === "products" && <ProductsTab />}
        {tab === "enquiries" && <EnquiriesTab />}
        {tab === "notify" && <NotifyTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
